/* eslint react/prop-types: 0 */

import { Suspense, lazy, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, connect } from 'react-redux';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import HistoryRouter from 'components/history-router/history-router';
import { isMobileOnly } from 'react-device-detect';
import Initialiser from 'initialiser';
import LogRocket from 'logrocket';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import Mixpanel from 'utils/mixpanel';
import { isAdmin, hasAuthToken, isEmpty, isImpelAdmin, isImpelMember } from 'utils/auth';
import { hasPublicView } from './utils/site-header';
import api from 'utils/api';
import mask from 'utils/mask';
import store from 'ducks/configureStore';
import { selectUser, fetchUser } from 'ducks/modules/user';
import Loader from 'components/loader/loader';
import Toaster from 'components/toaster/toaster';
import SiteHeader from 'components/site-header/site-header';
const Login = lazy(() => import('scenes/login/login'));
const Maintenance = lazy(() => import('scenes/maintenance/maintenance'));
const Home = lazy(() => import('scenes/home/home'));
const WorkList = lazy(() => import('scenes/work-list/work-list'));
const Work = lazy(() => import('scenes/work/work'));
const Dashboard = lazy(() => import('scenes/dashboard/dashboard'));
const Performance = lazy(() => import('scenes/performance/performance'));
const Inbox = lazy(() => import('scenes/inbox/inbox'));
const Licensing = lazy(() => import('scenes/licensing/licensing'));
const ProfileSettings = lazy(() => import('scenes/profile-settings/profile-settings'));
const ConsentForm = lazy(() => import('scenes/consent-form/consent-form'));
const MarkdownDocument = lazy(() => import('scenes/markdown-document/markdown-document'));
const ResetPassword = lazy(() => import('scenes/reset-password/reset-password'));
const RecoverPassword = lazy(() => import('scenes/recover-password/recover-password'));
const Error = lazy(() => import('scenes/error/error'));
const ResponsiveMessage = lazy(() => import('scenes/responsive-message/responsive-message'));
const SignUp = lazy(() => import('scenes/sign-up/sign-up-form.jsx'));
const MatchingDashboard = lazy(() => import('scenes/matching-dashboard'));
const MatchingDashboardSubscribe = lazy(() => import('scenes/matching-dashboard/matching-dashboard.subscribe'));
const MatchingReport = lazy(() => import('scenes/matching-report/matching-report.jsx'));
const PricingPage = lazy(() => import('scenes/pricing/pricing-page.jsx'));
const FileStatus = lazy(() => import('scenes/file-status/file-status.jsx'));
const Mandates = lazy(() => import('scenes/mandates/mandates'));
const AckStatus = lazy(() => import('scenes/ack-status/ack-status.jsx'));

import HubspotTracking from 'components/hubspot-tracking/hubspot-tracking';
import ErrorBoundary from 'components/error-boundary/error-boundary';
import Hubspot from 'utils/hubspot';
import { PersonaData, PersonaEnum } from './utils/personas';

import 'normalize.css';
import 'scss/index.scss';
import 'react-tooltip/dist/react-tooltip.css';

// Skip third-party SDK init when env vars are unset (e.g. CI smoke builds without
// production analytics secrets). The SDKs throw on undefined, which would prevent
// React from mounting; the suite would then fail on missing data-cy selectors.
if (process.env.LOGROCKET_ID) {
    LogRocket.init(process.env.LOGROCKET_ID);
}
if (process.env.GA_ID) {
    ReactGA.initialize(process.env.GA_ID);
}
if (process.env.GTM_ID) {
    TagManager.initialize({ gtmId: process.env.GTM_ID });
}

// monkey-patch console object to prevent any logging in the console in production
if (process.env.NODE_ENV === 'production') {
    for (const key in console) {
        if (typeof console[key] === 'function') {
            console[key] = () => {};
        }
    }
}

// listen for changes in local storage and re-fetch user in case of change to access_token
window.addEventListener(
    'storage',
    (e) => {
        if (e.key === 'access_token' && e.oldValue !== e.newValue) {
            store.dispatch(fetchUser());
        }
    },
    false,
);

// listen for secret key combo (`ctrl+b` 7 times in a row) to mask text as Big Tuna for demo purposes
let lastComboEvent = {};
let comboCount = 0;
document.addEventListener('keydown', function (event) {
    if (event.ctrlKey && event.key === 'b') {
        if (event.ctrlKey === lastComboEvent.ctrlKey && event.key === lastComboEvent.key) {
            comboCount++;
        } else {
            comboCount = 1;
        }
    }
    if (comboCount >= 7) {
        comboCount = 0;
        mask(selectUser());
    }
    lastComboEvent = event;
});

// Collect activity with google analytics
Initialiser.history.listen(({ location }) => {
    ReactGA.pageview(`${location.pathname}${location.search}`);
    Mixpanel.lib.track('Page View', { url: `${location.pathname}${location.search}` });
});

const computeRedirect = (props, location) => {
    if (process.env.MAINTENANCE === 'true') {
        return <Navigate to={{ pathname: '/maintenance' }} state={{ from: location }} replace />;
    }
    if (!hasAuthToken() && process.env.MAINTENANCE !== 'true') {
        // unauthenticated, redirect to login
        return <Navigate to={{ pathname: '/login' }} state={{ from: location }} replace />;
    } else if (props.fetching.indexOf('user') > -1 || isEmpty()) {
        // user object is being fetched
        return <Loader />;
    }

    if (isMobileOnly && selectUser().source_type === 'PB') {
        return <Navigate to={{ pathname: '/responsive-message' }} state={{ from: location }} replace />;
    }
    // redirect user to ts&cs form if they haven't yet accepted
    if (!selectUser().accepted_terms && location.pathname !== '/forms/terms-and-conditions') {
        return (
            <Navigate
                to={{ pathname: '/forms/terms-and-conditions', search: `redirect=${location.pathname}` }}
                state={{ from: location }}
                replace
            />
        );
    }
    // redirect user to privacy policy form if they haven't yet accepted
    if (
        !selectUser().accepted_privacy &&
        location.pathname !== '/forms/privacy-policy' &&
        location.pathname !== '/forms/terms-and-conditions'
    ) {
        return (
            <Navigate
                to={{ pathname: '/forms/privacy-policy', search: `redirect=${location.pathname}` }}
                state={{ from: location }}
                replace
            />
        );
    }
    if (
        location.pathname === '/draganddrop/pricing' &&
        selectUser().user_preferences &&
        selectUser().user_preferences.subscription?.drag_and_drop &&
        Object.keys(selectUser().user_preferences.subscription?.drag_and_drop).length > 0
    ) {
        return <Navigate to={{ pathname: '/draganddrop' }} state={{ from: location }} replace />;
    }
    let defaultPath = '/'; // By default our users go to the main view.
    if (selectUser() && selectUser().features) {
        if (
            // If they have access to the Licensing Messenger but not Copyright or the Matching Dashboard, they should go to the inbox.
            selectUser().features.licensing_messenger &&
            !selectUser().features.rights_management &&
            !selectUser().features.matching_dashboard
        ) {
            defaultPath = '/inbox';
        } else if (
            // If they have access to the Licensing Dashboard but not Copyright or the Matching Dashboard, they should go to the Licensing Dashboard.
            selectUser().features.streaming_licensing &&
            !selectUser().features.rights_management &&
            !selectUser().features.matching_dashboard
        ) {
            defaultPath = '/licensing';
        } else if (isImpelAdmin()) {
            defaultPath = '/works';
        } else if (
            // If they have access to the File Status but not the Copyright Dashboard, they should go to the File Status page.
            isImpelMember()
        ) {
            defaultPath = '/file-status';
        }

        // Redirect users away from /draganddrop if they do not have the feature enabled
        if (
            (location.pathname === '/draganddrop' || location.pathname.startsWith('/draganddrop/')) &&
            location.pathname !== '/draganddrop/pricing' &&
            location.pathname !== '/draganddrop/subscribe' &&
            !selectUser().features.matching_dashboard
        ) {
            return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
        }
        // Redirect users away from search and work pages if they do not have the feature enabled
        // if (
        //     (location.pathname === '/works' || location.pathname === '/work') &&
        //     !selectUser().features.rights_management
        // ) {
        //     return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
        // }
        // Redirect users away from file status if they do not have the feature enabled
        if (location.pathname === '/file-status' && !isImpelMember()) {
            return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
        }

        // if (
        //     location.pathname === '/' &&
        //     selectUser().features.file_status &&
        //     !selectUser().features.rights_management
        // ) {
        //     return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
        // }
    }

    // if already accepted ts&cs redirect away
    if (selectUser().accepted_terms && location.pathname === '/forms/terms-and-conditions') {
        // redirect to privacy form if not yet accepted
        if (!selectUser().accepted_privacy) {
            return <Navigate to={{ pathname: '/forms/privacy-policy' }} state={{ from: location }} replace />;
        }
        return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
    }
    // if already accepted privacy redirect away
    if (selectUser().accepted_privacy && location.pathname === '/forms/privacy-policy') {
        // redirect to email consent form if not yet accepted
        if (!selectUser().accepted_email) {
            return <Navigate to={{ pathname: '/forms/email-consent' }} state={{ from: location }} replace />;
        }
        return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
    }
    if (
        (defaultPath === '/inbox' ||
            defaultPath === '/licensing' ||
            defaultPath === '/file-status' ||
            defaultPath === '/works') &&
        location.pathname === '/'
    ) {
        return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
    }
    if (!isAdmin() && location.pathname.indexOf('/dashboard') > -1) {
        // redirect non-admins away from dashboard to home
        return <Navigate to={{ pathname: defaultPath }} state={{ from: location }} replace />;
    } else if (isAdmin() && location.pathname.indexOf('/dashboard') === -1) {
        // always redirect admins to dashboard
        return <Navigate to={{ pathname: '/dashboard' }} state={{ from: location }} replace />;
    }
    // safe to render component
    return null;
};

const mapStateToProps = (state) => ({
    user: state.user,
    upload: state.upload,
    fetching: state.fetching,
});

const mapDispatchToProps = (dispatch) => ({
    fetchUser: () => dispatch(fetchUser()),
});

const PrivateRoute = connect(
    mapStateToProps,
    mapDispatchToProps,
)(function PrivateRoute(props) {
    const location = useLocation();
    const mountedRef = useRef(false);

    useEffect(() => {
        const userMissing =
            (!props.user || Object.keys(props.user).length === 0) &&
            props.fetching.indexOf('user') === -1 &&
            process.env.MAINTENANCE !== 'true';
        if (!mountedRef.current) {
            mountedRef.current = true;
            if (process.env.LOGROCKET_ID) {
                LogRocket.init(process.env.LOGROCKET_ID);
            }
            if (userMissing) {
                props.fetchUser();
            } else {
                Hubspot.identify(props.user);
            }
        } else if (userMissing) {
            props.fetchUser();
        }
    });

    const redirect = computeRedirect(props, location);
    if (redirect) {
        return redirect;
    }
    return props.children;
});

/**
 * A public route that also acts as a private route for user checks, used on draganddrop public pages
 */
const SemiPrivateRoute = connect(
    mapStateToProps,
    mapDispatchToProps,
)(function SemiPrivateRoute(props) {
    const location = useLocation();

    // Redirect publishers away from matching dashboard. /draganddrop/subscribe is
    // intentionally accessible to PB users.
    if (
        selectUser() &&
        selectUser().source_type &&
        selectUser().source_type === 'PB' &&
        location.pathname.startsWith('/draganddrop') &&
        location.pathname !== '/draganddrop/subscribe'
    ) {
        return <Navigate to={{ pathname: '/' }} replace />;
    }

    if (location.pathname === '/sign-up/draganddrop') {
        if (hasAuthToken()) {
            const qs = new URLSearchParams(location.search);
            let pathname = '/';
            if (
                selectUser().user_preferences &&
                selectUser().user_preferences.subscription?.drag_and_drop &&
                Object.keys(selectUser().user_preferences.subscription?.drag_and_drop).length > 0
            ) {
                pathname = '/draganddrop';
            }
            if (qs.has('url')) {
                pathname = qs.get('url');
            }
            return <Navigate to={{ pathname }} state={{ from: location }} replace />;
        }
    }

    if (hasAuthToken()) {
        return <PrivateRoute>{props.children}</PrivateRoute>;
    }
    return props.children;
});

function TermsAndConditionsForm() {
    const location = useLocation();
    return (
        <ConsentForm
            getForm={() => api.getForm('terms-and-conditions')}
            checkboxLabel="I have read and accept the Terms and Conditions"
            consent={() => api.acceptForm('terms-and-conditions')}
            redirect={`/forms/privacy-policy${location.search}`}
            mustConsent={true}
        />
    );
}

function PrivacyPolicyForm() {
    const location = useLocation();
    return (
        <ConsentForm
            getForm={() => api.getForm('privacy-policy')}
            checkboxLabel="I have read and accept the Privacy Policy"
            consent={() => api.acceptForm('privacy-policy')}
            redirect={(() => {
                let pathname = '/';
                const qs = new URLSearchParams(location.search);
                if (qs.has('redirect')) {
                    pathname = qs.get('redirect');
                }
                return selectUser().accepted_email ? pathname : `/forms/email-consent${location.search}`;
            })()}
            mustConsent={true}
        />
    );
}

function EmailConsentForm() {
    const location = useLocation();
    return (
        <ConsentForm
            getForm={() => api.getForm('email-consent')}
            checkboxLabel="I consent to receive emails from Blokur"
            consent={() => api.acceptForm('email-consent')}
            reject={() => api.rejectForm('email-consent')}
            redirect={(() => {
                let pathname = '/';
                const qs = new URLSearchParams(location.search);
                if (qs.has('redirect')) {
                    pathname = qs.get('redirect');
                }
                return pathname;
            })()}
            mustConsent={false}
        />
    );
}

function SiteHeaderRoute() {
    const location = useLocation();
    return <SiteHeader publicView={hasPublicView({ hasAuth: hasAuthToken(), pathname: location.pathname })} />;
}

const queryClient = new QueryClient();

const notFound = (
    <Error
        code={404}
        title="Oops. The page you are looking for doesn't exist."
        description="You may have mistyped the address or the page may have been moved."
    />
);

const root = createRoot(document.getElementById('root'));
root.render(
    <QueryClientProvider client={queryClient}>
        <Provider store={store}>
            <HistoryRouter history={Initialiser.history}>
                <div className="root-container">
                    <title>Blòkur</title>
                    <SiteHeaderRoute />
                    <Suspense fallback={<Loader />}>
                        <div className="view-container">
                            <Routes>
                                <Route path="/maintenance" element={<Maintenance />} />
                                <Route path="/responsive-message" element={<ResponsiveMessage />} />
                                <Route
                                    path="/login"
                                    element={<Login maintenance={process.env.MAINTENANCE === 'true'} />}
                                />
                                <Route path="/login/reset-password" element={<ResetPassword />} />
                                <Route
                                    path="/login/recover-password"
                                    element={<RecoverPassword maintenance={process.env.MAINTENANCE === 'true'} />}
                                />
                                <Route
                                    path="/unavailable"
                                    element={
                                        <Error
                                            code={503}
                                            title="Service is Unavailable."
                                            description="Ooops! Looks like we are having some server issues."
                                        />
                                    }
                                />
                                <Route
                                    path="/error"
                                    element={
                                        <Error
                                            code={500}
                                            title="Internal Server Error"
                                            description="Ooops! Looks like we are having some server issues."
                                        />
                                    }
                                />
                                <Route
                                    path="/draganddrop/pricing"
                                    element={
                                        <PrivateRoute>
                                            <PricingPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/draganddrop/subscribe"
                                    element={
                                        <PrivateRoute>
                                            <MatchingDashboardSubscribe />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/draganddrop/*"
                                    element={
                                        <SemiPrivateRoute>
                                            <MatchingDashboard />
                                        </SemiPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/matching-dashboard/:bkUserId/:reportId/:filename"
                                    element={
                                        <PrivateRoute>
                                            <MatchingReport />
                                        </PrivateRoute>
                                    }
                                />
                                {/* Legacy 2-level report links (matching_dashboard/{bkUserId}/{file}, written
                                    before FRT-4847 required a report-type at upload) have only two path
                                    segments, so they miss the 3-param route above. Keep them reachable. */}
                                <Route
                                    path="/matching-dashboard/:bkUserId/:filename"
                                    element={
                                        <PrivateRoute>
                                            <MatchingReport />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/sign-up/draganddrop"
                                    element={
                                        <SemiPrivateRoute>
                                            <SignUp
                                                persona={PersonaEnum.MATCHING_DASHBOARD}
                                                personaData={PersonaData.MATCHING_DASHBOARD}
                                            />
                                        </SemiPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/sign-up/content-creator"
                                    element={
                                        <SemiPrivateRoute>
                                            <SignUp
                                                persona={PersonaEnum.CONTENT_CREATOR}
                                                personaData={PersonaData.CONTENT_CREATOR}
                                            />
                                        </SemiPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/sign-up/dsp"
                                    element={
                                        <SemiPrivateRoute>
                                            <SignUp persona={PersonaEnum.DSP} personaData={PersonaData.DSP} />
                                        </SemiPrivateRoute>
                                    }
                                />
                                <Route
                                    path="/"
                                    element={
                                        <PrivateRoute>
                                            <Home />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/works"
                                    element={
                                        <PrivateRoute>
                                            <WorkList />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/work/:bkWorkId"
                                    element={
                                        <PrivateRoute>
                                            <Work />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/work/:bkWorkId/:countryId"
                                    element={
                                        <PrivateRoute>
                                            <ErrorBoundary isPage={true}>
                                                <Work />
                                            </ErrorBoundary>
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/*"
                                    element={
                                        <PrivateRoute>
                                            <Dashboard />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/performance/*"
                                    element={
                                        <PrivateRoute>
                                            <Performance />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/inbox/*"
                                    element={
                                        <PrivateRoute>
                                            <Inbox />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/licensing/*"
                                    element={
                                        <PrivateRoute>
                                            <Licensing />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/file-status/*"
                                    element={
                                        <PrivateRoute>
                                            <FileStatus />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/ack-status/*"
                                    element={
                                        <PrivateRoute>
                                            <AckStatus />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/mandates/*"
                                    element={
                                        <PrivateRoute>
                                            <Mandates />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/profile/settings"
                                    element={
                                        <PrivateRoute>
                                            <ProfileSettings />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/forms/terms-and-conditions"
                                    element={
                                        <PrivateRoute>
                                            <TermsAndConditionsForm />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/forms/privacy-policy"
                                    element={
                                        <PrivateRoute>
                                            <PrivacyPolicyForm />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/forms/email-consent"
                                    element={
                                        <PrivateRoute>
                                            <EmailConsentForm />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/documents/terms-and-conditions"
                                    element={
                                        <MarkdownDocument getDocument={() => api.getForm('terms-and-conditions')} />
                                    }
                                />
                                <Route
                                    path="/documents/privacy-policy"
                                    element={<MarkdownDocument getDocument={() => api.getForm('privacy-policy')} />}
                                />
                                <Route
                                    path="/documents/cookies-policy"
                                    element={<MarkdownDocument getDocument={() => api.getForm('cookies-policy')} />}
                                />
                                <Route
                                    path="/forbidden"
                                    element={
                                        <PrivateRoute>
                                            <Error
                                                code={403}
                                                title="Hmm. You don't have permission to view this page."
                                                description=""
                                            />
                                        </PrivateRoute>
                                    }
                                />
                                <Route path="/notfound" element={<PrivateRoute>{notFound}</PrivateRoute>} />
                                <Route path="*" element={<PrivateRoute>{notFound}</PrivateRoute>} />
                            </Routes>
                        </div>
                    </Suspense>
                    <Toaster />
                </div>
            </HistoryRouter>
            <HubspotTracking />
        </Provider>
    </QueryClientProvider>,
);
